import { useState, useEffect } from "react";
import { Space, Card, Switch, Typography, message, Spin } from "antd";
import { ZoneConfig, DeviceData, LastMessage, SwitchOnOff } from "../../model/model";
import SessionStore from "../../model/SessionStore";

const { Text } = Typography;

interface IProps {
  zoneConfig: ZoneConfig;
}

function ZoneSwitches(props: IProps) {
  const [device, setDevice] = useState<DeviceData | undefined>(undefined);
  const [lastMessage, setLastMessage] = useState<LastMessage | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  const { zoneConfig } = props;

  useEffect(() => {
    if (!zoneConfig.devId) {
      setLoading(false);
      return;
    }

    const bearer = SessionStore.getBearer();
    Promise.all([
      SessionStore.getClient().getDevice(zoneConfig.devId, bearer),
      SessionStore.getClient().getDeviceLastMetric(zoneConfig.devId, bearer),
    ]).then(([devResp, metricResp]) => {
      setDevice(devResp.data);
      setLastMessage(metricResp.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [zoneConfig.devId]);

  const handleSwitch = (coilIndex: number, checked: boolean) => {
    if (!zoneConfig.devId) return;
    const cmd: SwitchOnOff = {
      coil: coilIndex,
      on: checked,
      devId: zoneConfig.devId,
    };
    SessionStore.getClient().switchOnOff(cmd, SessionStore.getBearer()).then(() => {
      messageApi.success(`Switch ${coilIndex + 1} turned ${checked ? "ON" : "OFF"}`);
      // Update local state
      setLastMessage((prev) => {
        if (!prev) return prev;
        const newCoils = [...(prev.coils || [])];
        newCoils[coilIndex] = checked;
        return { ...prev, coils: newCoils };
      });
    }).catch((err) => {
      messageApi.error(`Failed to switch: ${err.message}`);
    });
  };

  if (loading) {
    return <Spin />;
  }

  if (!device) {
    return <Text type="secondary">No device assigned to this zone config.</Text>;
  }

  const coilsCount = device.coils ? (Array.isArray(device.coils) ? device.coils.length : Number(device.coils)) : 0;

  if (coilsCount === 0) {
    return <Text type="secondary">Device has no switches (coils).</Text>;
  }

  const coilStatuses: boolean[] = lastMessage?.coils || [];

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {contextHolder}
      <Text strong>Device: {device.name}</Text>
      <Space wrap>
        {Array.from({ length: coilsCount }, (_, i) => (
          <Card key={i} size="small" style={{ minWidth: 120 }}>
            <Space direction="vertical" align="center">
              <Text>{device.coils?.[i] || `Switch ${i + 1}`}</Text>
              <Switch
                checked={!!coilStatuses[i]}
                onChange={(checked) => handleSwitch(i, checked)}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </Space>
          </Card>
        ))}
      </Space>
    </Space>
  );
}

export default ZoneSwitches;
